import { useRef, useState, useEffect } from 'react';
import { Camera, Eraser, Check } from 'lucide-react';

export const DeliveryProofCapture = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * (canvas.width / rect.width), y: (clientY - rect.top) * (canvas.height / rect.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setHasSignature(true);
    emit(photo, canvasRef.current.toDataURL('image/png'));
  };

  const clearSig = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    emit(photo, null);
  };

  const emit = (p, s) => { if (onChange) onChange({ photo: p, signature: s }); };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto(reader.result);
      emit(reader.result, hasSignature ? canvasRef.current.toDataURL('image/png') : null);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3" data-testid="delivery-proof-capture">
      <div>
        <p className="text-xs font-semibold text-gray-700 mb-1.5">Delivery photo</p>
        {photo ? (
          <div className="relative">
            <img src={photo} alt="Delivery proof" className="w-full h-32 object-cover rounded-lg border" />
            <label className="absolute bottom-2 right-2 bg-white/90 text-xs font-medium px-2 py-1 rounded-md border cursor-pointer">
              Retake
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} data-testid="proof-photo-input" />
            </label>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center gap-1 h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer text-gray-500 hover:border-cyan-400">
            <Camera className="h-5 w-5" />
            <span className="text-xs">Take a photo</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} data-testid="proof-photo-input" />
          </label>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs font-semibold text-gray-700">Customer signature</p>
          <button onClick={clearSig} className="flex items-center gap-1 text-xs text-gray-500" data-testid="proof-sig-clear">
            <Eraser className="h-3 w-3" /> Clear
          </button>
        </div>
        <canvas
          ref={canvasRef}
          width={320}
          height={120}
          className="w-full h-28 bg-white border-2 border-gray-200 rounded-lg touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="proof-signature-canvas"
        />
        {hasSignature && (
          <p className="flex items-center gap-1 text-xs text-green-600 mt-1"><Check className="h-3 w-3" /> Signature captured</p>
        )}
      </div>
    </div>
  );
};
